import { Select as AntSelect } from "antd";
import { Option, ReadonlyArray, Runtime } from "fp";

import React from "react";
import { useRouteMatch } from "react-router-dom";

import { Company } from "~/Company";
import * as Data from "~/Data";
import { Device } from "~/Device";
import { gql, Graph } from "~/Graph";
import { HoursOfService } from "~/HoursOfService";
import { Human } from "~/Human";
import { ID } from "~/ID";
import { Location } from "~/Location";
import { Order } from "~/Order";
import { Search as GenericSearch } from "~/Search";
import { Time } from "~/Time";
import * as IndividualDriver from "~/IndividualDriver";

import * as Filter from "./Filter";
import * as Name from "./Name";
import { Page } from "./Page";
import * as Search from "./Search";
import * as Sort from "./Sort";
import * as Status from "./Status";

import { NewBranch as Branch } from "~/Branch";

export * as Action from "./Action";
export * from "./AddEdit";
export * as Count from "./Count";
export * as Filter from "./Filter";
export * as Name from "./Name";
export * from "./Overview";
export * from "./Page";
export * as Pairing from "./Pairing";
export * as Request from "./Request";
export * as Search from "./Search";
export * from "./Shift";
export * as Sort from "./Sort";
export * as Status from "./Status";
export { Table } from "./Table";

export type Driver = Data.Reference.Reference &
  Partial<Location & Location.State & HoursOfService & Device> & {
    readonly username?: string;
    readonly email?: string;
    readonly phone?: string;
    // TODO: Move into `Driver.License`
    readonly license?: string;
    readonly homeOffice?: Company.Location;
    readonly name?: Name.Name;
    readonly status?: Status.Status;
    readonly hasBerth?: boolean;
    readonly time?: Time.Zone;
    readonly branch?: Branch.Branch;
    readonly inactiveDate?: string;
  };

export namespace Driver {
  export const formatStringForExport = (driver: Driver) =>
    driver.name ? `${driver.id} / ${driver.name.display}` : driver.id;
}

export const order = () =>
  Order.fromCompare<Driver>((a, b) =>
    !a.name && !b.name
      ? Order.string().compare(a.id, b.id)
      : Order.optionally(Name.compare)(a.name, b.name)
  );

export const { equals, compare } = order();

export const toString = ({ id, name }: Driver): string =>
  (name && Name.toString(name)) || `Driver ${id}`;

export const toReadableID = ({
  id,
  name,
}: Pick<Driver, "id" | "name">): string =>
  pipe(
    [name?.first, name?.last, id],
    ReadonlyArray.filterMap(
      flow(
        Option.fromNullable,
        Option.map((part) => part.toLocaleLowerCase())
      )
    ),
    (parts) => parts.join("-").replace(/[()]/g, " ")
  );

export const toSearch: GenericSearch.From<Driver> = (driver) =>
  !driver
    ? GenericSearch.empty()
    : GenericSearch.join(
        driver.status ? Status.toSearch(driver.status) : GenericSearch.empty(),
        driver.name ? Name.toSearch(driver.name) : GenericSearch.empty(),
        driver.device
          ? Device.toSearch({ device: driver.device })
          : GenericSearch.empty(),
        driver.hoursOfService
          ? HoursOfService.toSearch({ hoursOfService: driver.hoursOfService })
          : GenericSearch.empty()
      );

export const useStore = (): Data.Store.Store<Driver> =>
  Data.Store.useSelector(({ drivers: { store } }) => store);

export const useFilteredStore = (): Data.Store.Store<Driver> =>
  Data.Store.useSelector(({ drivers: { filteredStore } }) => filteredStore);

export function useReferenceFromRoute(): Option<Data.Reference.Reference> {
  return pipe(
    useRouteMatch<{ readonly readableID?: string }>(Page.Route.definition()),
    Option.fromNullable,
    Option.mapNullable((match) => match.params?.readableID),
    Option.chain((readableID) => ReadonlyArray.last(readableID.split("-"))),
    Option.map((id) => ({ id }))
  );
}

export function useFromReference<A>(
  get: Data.Reference.Get.When<Driver, A>
): A {
  return pipe(useStore(), Data.Store.Get.fromReference(get));
}

export type Fragment = Graph.Generated.DriverFragment;
export namespace Fragment {
  export const document = () => gql`
    ${Human.Name.Fragment.document()}

    fragment Driver on Driver {
      id
      name {
        ...Name
      }
    }
  `;

  export const decode = (original: Fragment): Driver => ({
    ...ID.from(original.id),
    ...Human.Name.Fragment.decode(original.name),
  });
}

export namespace State {
  export type State = {
    readonly store: Data.Store.Store<Driver>;
    readonly filteredStore: Data.Store.Store<Driver>;
    readonly sort: Sort.State.State;
    readonly search: Search.Search;
    readonly filter: Filter.Filter;
    readonly branchFilter: Filter.BranchFilter;
    readonly driverId: IndividualDriver.DriverId.DriverId;
    readonly individualStore: Data.Store.Store<Driver>;
  };

  export const initial = (): State => ({
    sort: Sort.State.initial(),
    store: Data.Store.initial(),
    filteredStore: Data.Store.initial(),
    individualStore: Data.Store.initial(),
    search: Search.State.initial(),
    filter: Filter.State.initial(),
    branchFilter: Filter.State.Branch.initial(),
    driverId: IndividualDriver.DriverId.State.initial(),
  });
}

export namespace Response {
  export const runtime = () =>
    Runtime.intersection([
      Runtime.strict({
        driver_id: Runtime.number(),
        username: Runtime.nullable(Runtime.string()),
        email_address: Runtime.nullable(Runtime.string()),
        phone_number: Runtime.nullable(Runtime.string()),
        license_number: Runtime.nullable(Runtime.string()),
        license_state: Runtime.nullable(Runtime.string()),
        sleeper_berth: Runtime.nullable(Runtime.boolean()),
        timezone: Runtime.nullable(Runtime.string()),
        hours_of_service_type_id: Runtime.nullable(Runtime.number()),
        exemption_type_id: Runtime.nullable(Runtime.number()),
        last_latitude: Runtime.nullable(Runtime.number()),
        last_longitude: Runtime.nullable(Runtime.number()),
        reporting_location: Runtime.nullable(
          Company.Location.Response.runtime()
        ),
        branch: Runtime.nullable(Branch.Response.runtime()),
        inactive_date: Runtime.nullable(Runtime.string()),
      }),
      Runtime.intersection([
        Name.Response.runtime(),
        Device.Response.runtime(),
        HoursOfService.Timers.Response.runtime(),
        Status.Response.runtime(),
      ]),
    ]);

  export const decode = (response: Runtime.ToType<typeof runtime>): Driver => {
    const state = response.license_state && {
      state: { abbreviation: response.license_state },
    };

    const homeOffice = response.reporting_location && {
      homeOffice: Company.Location.Response.decode(response.reporting_location),
    };

    const location = typeof response.last_latitude === "number" &&
      typeof response.last_longitude === "number" && {
        location: {
          gps: {
            latitude: response.last_latitude,
            longitude: response.last_longitude,
          },
        },
      };

    const hoursOfServiceType = !!response.hours_of_service_type_id && {
      type: ID.from(response.hours_of_service_type_id),
    };

    const exemption = !!response.exemption_type_id && {
      exemption: ID.from(response.exemption_type_id),
    };

    const timers = response.timer_cycle !== undefined &&
      response.timer_shift !== undefined &&
      response.timer_drive !== undefined &&
      response.timer_nobreak !== undefined && {
        timers: {
          cycle: { timer: response.timer_cycle },
          shift: { timer: response.timer_shift },
          drive: { timer: response.timer_drive },
          break: { timer: response.timer_nobreak },
        },
      };

    const hoursOfService = (exemption || hoursOfServiceType || timers) && {
      hoursOfService: { ...hoursOfServiceType, ...exemption, ...timers },
    };

    return {
      id: `${response.driver_id}`,
      username: response.username ?? undefined,
      email: response.email_address ?? undefined,
      phone: response.phone_number ?? undefined,
      license: response.license_number ?? undefined,
      ...state,
      ...homeOffice,
      ...location,
      ...hoursOfService,
      name: Name.Response.decode(response),
      ...Device.Response.decode(response),
      status: Status.Response.decode(response),
      hasBerth: response.sleeper_berth ?? false,
      time: response.timezone ? { zone: response.timezone } : undefined,
      branch: response.branch
        ? Branch.Response.decode(response.branch)
        : undefined,
        inactiveDate: response.inactive_date ?? undefined,
    };
  };
}

export type Drivers = { readonly drivers: readonly Driver[] };
export namespace Drivers {
  export const empty = (): Drivers => ({ drivers: [] });
  export const use = (): Drivers =>
    pipe(
      useStore(),
      Data.Store.toCache,
      Data.Cache.toList((id, driver) => ({ ...driver, id })),
      (drivers) => ({ drivers })
    );

  export function Select({ selected, select, clear, ...props }: Select.Props) {
    const options = Select.Options.fromDrivers(use());
    const value = pipe(
      selected?.drivers ?? [],
      ReadonlyArray.map(({ id }) => id)
    );

    const onChange = (ids: readonly string[]) =>
      pipe(
        options,
        ReadonlyArray.filterMap(({ driver }) =>
          ids.includes(driver.id) ? Option.some(driver) : Option.none
        ),
        (drivers) => select({ drivers })
      );

    const filterOption = (value: string, option: Select.Option) =>
      GenericSearch.matches(GenericSearch.Match.string(value))(
        toSearch(option.driver)
      );

    type AntSelectProps = React.ComponentProps<typeof AntSelect>;
    return (
      <AntSelect
        {...props}
        showArrow
        mode="multiple"
        filterOption={filterOption as AntSelectProps["filterOption"]}
        // TODO: Implement when using React Query
        // loading={Remote.isLoading(locations)}
        value={value as AntSelectProps["value"]}
        options={(options as never) as AntSelectProps["options"]}
        onChange={onChange as AntSelectProps["onChange"]}
        onClear={clear}
      />
    );
  }

  export namespace Select {
    export type Props = Controls &
      Pick<
        React.ComponentProps<typeof AntSelect>,
        "bordered" | "placeholder" | "style"
      >;

    export type Controls = {
      readonly selected?: Partial<Drivers>;
      readonly select: (drivers: Partial<Drivers>) => void;
      readonly clear: () => void;
    };

    export namespace Controls {
      export const use = (): Controls => {
        const [selected, setSelected] = React.useState<Drivers>(empty());
        return {
          selected,
          clear: () => setSelected(empty()),
          select: ({ drivers }: Partial<Drivers>) =>
            pipe(
              [...selected.drivers, ...(drivers ?? [])],
              ReadonlyArray.uniq({ equals }),
              (drivers) => setSelected({ drivers })
            ),
        };
      };
    }

    export type Option = {
      readonly driver: Driver;
      readonly value: string;
      readonly label: string;
    };

    export type Options = readonly Option[];
    export namespace Options {
      export const fromDrivers = ({ drivers }: Drivers): Options =>
        pipe(
          drivers,
          ReadonlyArray.sort({ equals, compare }),
          ReadonlyArray.map((driver) => ({
            driver,
            value: driver.id,
            label: toString(driver),
          }))
        );
    }
  }
}
